import React, { useEffect } from "react";
import { Button, Typography, Grid } from "@mui/material";
import ErrorBoundary from "pages/dashboard/admin/import/interest_imports/components/boundary";
import FieldsNew from "pages/dashboard/admin/import/interest_imports/components/fields-new";
import { StepProps } from "..";
import { useStepData } from "../context";
import StepLayout from "./layout";
import { validateStep } from "./validation";

const PATTERN_MAPPINGS = {
  // Personal Information
  personal: {
    patterns: {
      "^secondary phone extension$": "household_member_0_secondary_phone_ext",
      "^secondary phone$": "household_member_0_secondary_phone",
      "^secondary applicant first name$": "household_member_1_first_name",
      "^secondary applicant last name$": "household_member_1_last_name",
      "fname|first name": "household_member_0_first_name",
      "lname|last name": "household_member_0_last_name",
      "phone": {
        "contact": "contact_primary_phone",
        "secondary": "household_member_0_secondary_phone",
        "default": "household_member_0_primary_phone",
      },
      "ext": {
        "contact": "contact_primary_phone_ext",
        "default": "household_member_0_secondary_phone_ext",
      },
      "e-mail|email": {
        "contact": "contact_email",
        "default": "household_member_0_email",
      },
    },
  },
  // Address Information
  address: {
    patterns: {
      "address 1": {
        "mailing|mail": "mailing_address1",
        "default": "residence_address1",
      },
      "address 2": {
        "mailing|mail": "mailing_ste_number",
        "default": "residence_ste_number",
      },
      "city": {
        "mailing|mail": "mailing_city",
        "default": "residence_city",
      },
      "state": {
        "mailing|mail": "mailing_state",
        "default": "residence_state",
      },
      "zip|postal": {
        "mailing|mail": "mailing_zip",
        "default": "residence_zip",
      },
    },
  },
  // Unit Information
  unit: {
    patterns: {
      "unit #|unit number": "movein_unit_no",
      "^unit size$": "unit_size",
      "studio": "unit_size_studio",
      "sro": "unit_size_sro",
      "1bdr|1br|1 bdr|1 br": {
        "den": "unit_size_1br_den",
        "default": "unit_size_1br",
      },
      "2br|2 bdr|2 br|2bdr": "unit_size_2br",
      "3br|3 bdr|3 br|3bdr": "unit_size_3br",
      "4br|4 bdr|4 br|4bdr": "unit_size_4br",
      "5br|5 bdr|5 br|5bdr": "unit_size_5br",
    },
  },
  // Other Information
  other: {
    patterns: {
      "date of birth|applicant date of birth|birth date|\\bdob\\b": "household_member_0_dob",
      "^add to notes$|^add notes$": "add_notes",
      "submit date|date": "date",
      "submit time|time": "time",
      "position": "position_number",
      "income": "total_income",
      "household count|household size|hh size|hh count": "household_count",
      "voucher": "household_has_housing_authority_voucher",
      "removed\\?": "denied",
      "moved in\\?": "moved_in",
    },
  },
};

type PatternMapping = {
  [key: string]: string | { [key: string]: string };
};

const findSubPatternMatch = (
  normalized: string,
  value: { [key: string]: string },
): string | null => {
  for (const [subPattern, subValue] of Object.entries(value)) {
    if (subPattern === "default" || new RegExp(subPattern).test(normalized)) {
      return subValue;
    }
  }
  return null;
};

const findMatchingValue = (normalized: string, mapping: PatternMapping): string | null => {
  for (const [pattern, value] of Object.entries(mapping)) {
    if (new RegExp(pattern).test(normalized)) {
      if (typeof value === "object") {
        const match = findSubPatternMatch(normalized, value);
        if (match) return match;
      } else {
        return value as string;
      }
    }
  }
  return null;
};

export const guessMapping = (label: string): string => {
  const normalized = label.trim().toLowerCase();
  for (const category of Object.values(PATTERN_MAPPINGS)) {
    const match = findMatchingValue(normalized, category.patterns);
    if (match) return match;
  }
  return "none";
};

// eslint-disable-next-line max-lines-per-function
const StepMatch: React.FC<StepProps> = () => {
  const { stepData, setStepData, setStepName } = useStepData();
  const [highlightUnmatched, setHighlightUnmatched] = React.useState(false);

  if (!validateStep(stepData, "match")) {
    return null;
  }

  const applyAutoMatch = (inputHeaders: string[]) => {
    // Collect already assigned fields from stepData.mapping
    const usedFields = new Set<string>(
      Object.values(stepData.mapping).map(mapping => mapping.field),
    );

    const newMapping = inputHeaders.reduce((acc, header) => {
      if (!stepData.mapping[header]) {
        let guessed = guessMapping(header);
        // If guessed field is already used, mark as "none"
        if (guessed !== "none" && usedFields.has(guessed)) {
          guessed = "none";
        }
        // If the guess is valid, add it to the used set
        if (guessed !== "none") {
          usedFields.add(guessed);
        }
        acc[header] = { field: guessed, format: "" };
      }
      return acc;
    }, {} as Record<string, { field: string, format: string }>);

    setStepData({
      ...stepData,
      mapping: {
        ...stepData.mapping,
        ...newMapping,
      },
    });

    setHighlightUnmatched(true);
  };


  const allMappedCount = Object.keys(stepData.mapping).length;
  // Excluding Deleted and Passthrough
  const mappedCount = Object.keys(stepData.mapping).filter(
    key => ["none", "passthrough"].includes(stepData.mapping[key].field),
  ).length;

  const { data: [candidate] } = stepData;

  const headers = Object.keys(candidate);

  useEffect(() => {
    setStepName("Match");
  }, []);

  return <StepLayout title="Match spreadsheet columns to Haven Connect fields"
    description={<>
      <Typography variant="body1">
        Match spreadsheet columns to application fields in Haven Connect
        to ensure all key information is imported.
      </Typography>
      <ul>
        <li><Typography variant="body1">Columns marked as <strong>Skip</strong> will not be imported.</Typography></li>
        <li><Typography variant="body1">Columns marked as <strong>Add to Notes</strong> will be imported as notes.</Typography></li>
        <li><Typography variant="body1">Applications will be imported as <strong>Active</strong> unless marked as <strong>Removed</strong> or <strong>Moved In</strong>.</Typography></li>
        <li><Typography variant="body1">At least 3 columns must be mapped to import applications.</Typography></li>
      </ul>
    </>}>
    <Grid size={{ xs: 12 }}>
      <Button onClick={() => applyAutoMatch(headers)} variant="contained" color="primary">
        Auto-match columns
      </Button>
      <Typography variant="body1">
        If we can&apos;t match a column for you, we&apos;ll highlight it in red.
      </Typography>
    </Grid>
    <Grid size={{ xs: 12 }}>
      <ErrorBoundary message="An unexpected error was encountered while displaying the contents of the CSV.">
        <FieldsNew
          state={stepData}
          onSetMapping={(from, to) =>
            setStepData({
              ...stepData,
              mapping: { ...stepData.mapping, [from]: { field: to, format: "" } },
            })
          }
          highlightUnmatched={highlightUnmatched}
        />
      </ErrorBoundary>
    </Grid>
    <Grid size={{ xs: 12 }}>
      <Typography variant="body1">
        {stepData.data.length} applications were recognized in this file
      </Typography>
      <Typography variant="body1">
        {allMappedCount - mappedCount} columns will be imported. {
          headers.length - (allMappedCount - mappedCount)} columns will not be imported.
      </Typography>
    </Grid>
  </StepLayout>;
};

export default StepMatch;
